/**
 * Inspired by velten on 11.02.17.
 * https://www.npmjs.com/package/multi-regexp2
 */

/**
 * Adds brackets before and after a part of string
 * @param str string the hole regex string
 * @param start int marks the position where ( should be inserted
 * @param end int marks the position where ) should be inserted
 * @param groupsAdded int defines the offset to the original string because of inserted brackets
 * @return {string}
 */
const addGroupToRegexString = (str: string, start: number, end: number, groupsAdded: number) => {
  start += groupsAdded * 2;
  end += groupsAdded * 2;
  return `${str.substring(0, start)}(${str.substring(start, end + 1)})${str.substring(end + 1)}`;
};

const GROUP_TESTER = /(\(\?:)|(\((?:\?<.*?>)?)|(\\\()|(\\\))|(\(\?)|(\()|(\)(?:\{\d+,?\d*}|[*+?])?\??)/g;

interface GroupData {
  name?: string;
  index: number;
  previousGroups: number[];
}

/**
 * converts the given regex to a regex where all not captured string are going to be captured
 * it along sides generates a mapper which maps the original group index to the shifted group offset and
 * generates a list of groups indexes (including new generated capturing groups)
 * which have been closed before a given group index (unshifted)
 *
 * Example:
 * regexp: /a(?: )bc(def(ghi)xyz)/g => /(a(?: )bc)((def)(ghi)(xyz))/g
 * groupIndexMapper: {'1': 2, '2', 4}
 * previousGroupsForGroup: {'1': [1], '2': [1, 3]}
 */
const fillGroups = (regex: RegExp) => {
  let regexString: string;
  let modifier: string;
  if (regex.source && regex.flags) {
    regexString = regex.source;
    modifier = regex.flags;
  } else {
    regexString = regex.toString();
    modifier = regexString.substring(regexString.lastIndexOf(regexString[0]) + 1); // sometimes order matters ;)
    regexString = regexString.substr(1, regex.toString().lastIndexOf(regexString[0]) - 1);
  }
  // regexp is greedy so it should match (? before ( right?
  // brackets may be not quoted by \
  // closing bracket may look like: ), )+, )+?, ){1,}?, ){1,1111}?

  let modifiedRegex = regexString;

  let lastGroupStartPosition = -1;
  let lastGroupEndPosition = -1;
  let lastNonGroupStartPosition = -1;
  let lastNonGroupEndPosition = -1;
  let groupsAdded = 0;
  let groupCount = 0;
  let matchArr: RegExpMatchArray | null;
  const nonGroupPositions = [];
  const groupPositions = [];
  const groupNumber: number[] = [];
  const groups: GroupData[] = [];
  let currentLengthIndexes: number[] = [];
  while ((matchArr = GROUP_TESTER.exec(regexString)) !== null) {
    let capture = false;

    if (matchArr[3] || matchArr[4]) {
      // ignore escaped brackets \(, \)
    } else if (matchArr[5]) {
      // non capturing group (?
      const index = (matchArr.index || 0) + matchArr[0].length - 1;

      lastNonGroupStartPosition = index;
      //@ts-ignore
      nonGroupPositions.push(index);
    } else if (matchArr[6] || matchArr[2]) {
      // capturing group (
      capture = true;

      // there was a group of text before this group
      if (groups.length === 0 && (matchArr.index || 0) > 0) {
        modifiedRegex = addGroupToRegexString(modifiedRegex, 0, (matchArr.index || 0) - 1, groupsAdded);
        groupsAdded++;
        lastGroupEndPosition = (matchArr.index || 0) - 1;
        currentLengthIndexes.push(groupCount + groupsAdded);
      }
    } else if (matchArr[7]) {
      // closing bracket ), )+, )+?, ){1,}?, ){1,1111}?
      const index = (matchArr.index || 0) + matchArr[0].length - 1;

      if (
        (groupPositions.length && !nonGroupPositions.length) ||
        groupPositions[groupPositions.length - 1] > nonGroupPositions[nonGroupPositions.length - 1]
      ) {
        if (lastGroupStartPosition < lastGroupEndPosition && lastGroupEndPosition < index - 1) {
          modifiedRegex = addGroupToRegexString(modifiedRegex, lastGroupEndPosition + 1, index - 1, groupsAdded);
          groupsAdded++;
          //lastGroupEndPosition = index - 1; will be set anyway
          currentLengthIndexes.push(groupCount + groupsAdded);
        }

        groupPositions.pop();
        lastGroupEndPosition = index;

        const toPush = groupNumber.pop() || 0;
        currentLengthIndexes.push(toPush);
        currentLengthIndexes = currentLengthIndexes.filter((index) => index <= toPush);
      } else if (nonGroupPositions.length) {
        nonGroupPositions.pop();
        lastNonGroupEndPosition = index;
      }
    }

    if (capture) {
      const index = matchArr.index || 0;

      const lastGroupPosition = Math.max(lastGroupStartPosition, lastGroupEndPosition);

      // if a (? is found add ) before it
      if (lastNonGroupStartPosition > lastGroupPosition) {
        // check if between ) of capturing group lies a non capturing group
        if (lastGroupPosition < lastNonGroupEndPosition) {
          // add groups for x1 and x2 on (?:()x1)x2(?:...
          if (lastNonGroupEndPosition - 1 - (lastGroupPosition + 1) > 0) {
            modifiedRegex = addGroupToRegexString(
              modifiedRegex,
              lastGroupPosition + 1,
              lastNonGroupEndPosition - 1,
              groupsAdded,
            );
            groupsAdded++;
            lastGroupEndPosition = lastNonGroupEndPosition - 1; // imaginary position as it is not in regex but modifiedRegex
            currentLengthIndexes.push(groupCount + groupsAdded);
          }

          if (lastNonGroupStartPosition - 1 - (lastNonGroupEndPosition + 1) > 0) {
            modifiedRegex = addGroupToRegexString(
              modifiedRegex,
              lastNonGroupEndPosition + 1,
              lastNonGroupStartPosition - 2,
              groupsAdded,
            );
            groupsAdded++;
            lastGroupEndPosition = lastNonGroupStartPosition - 1; // imaginary position as it is not in regex but modifiedRegex
            currentLengthIndexes.push(groupCount + groupsAdded);
          }
        } else {
          modifiedRegex = addGroupToRegexString(
            modifiedRegex,
            lastGroupPosition + 1,
            lastNonGroupStartPosition - 2,
            groupsAdded,
          );
          groupsAdded++;
          lastGroupEndPosition = lastNonGroupStartPosition - 1; // imaginary position as it is not in regex but modifiedRegex
          currentLengthIndexes.push(groupCount + groupsAdded);
        }

        // if necessary also add group between (? and opening bracket
        if (index > lastNonGroupStartPosition + 2) {
          modifiedRegex = addGroupToRegexString(modifiedRegex, lastNonGroupStartPosition + 2, index - 1, groupsAdded);
          groupsAdded++;
          lastGroupEndPosition = index - 1; // imaginary position as it is not in regex but modifiedRegex
          currentLengthIndexes.push(groupCount + groupsAdded);
        }
      } else if (lastGroupPosition >= 0 && lastGroupPosition < index - 1) {
        modifiedRegex = addGroupToRegexString(modifiedRegex, lastGroupPosition + 1, index - 1, groupsAdded);
        groupsAdded++;
        lastGroupEndPosition = index - 1; // imaginary position as it is not in regex but modifiedRegex
        currentLengthIndexes.push(groupCount + groupsAdded);
      }

      let name: string | undefined = undefined;

      groupCount++;
      if (matchArr[2]) {
        name = matchArr[2].replace(/\(\?<(.*)>/, "$1");
      }
      lastGroupStartPosition = index;
      // @ts-ignore
      groupPositions.push(index);
      // @ts-ignore
      groupNumber.push(groupCount + groupsAdded);
      groups.push({
        name: name,
        index: groupCount + groupsAdded,
        previousGroups: currentLengthIndexes.slice(),
      });
    }
  }
  const groupSorter = (a: GroupData, b: GroupData, aIndex: number, bIndex: number): number => {
    // groupNames contains a map of groupIndex -> name
    // if a group has a name that is "$<int>" then we need to remove the $ and parse it as int
    // and then order by that

    const aName = a.name;
    const bName = b.name;

    if (aName && bName) {
      const aNameInt = Number.parseInt(aName.replace("$", ""));
      const bNameInt = Number.parseInt(bName.replace("$", ""));
      if (!Number.isNaN(aNameInt) && !Number.isNaN(bNameInt)) {
        return aNameInt - bNameInt;
      }
    }
    return aIndex - bIndex;
  };

  groups.sort(groupSorter as any);

  return { regexp: new RegExp(modifiedRegex, modifier), groups };
};

export interface ExecGroupResult {
  match: string;
  start: number;
  end: number;
}

export interface ExecGroupAllResult extends Array<ExecGroupResult> {
  fullMatch: RegExpMatchArray;
}

export default class MultiRegExp {
  private readonly rawRegExp: string;
  private readonly regexp: RegExp;
  private readonly groups: GroupData[];

  constructor(baseRegExp: RegExp) {
    this.rawRegExp = baseRegExp.source;
    const { regexp, groups } = fillGroups(baseRegExp);
    this.regexp = regexp;
    this.groups = groups;
  }

  execForAllGroups(string: string): ExecGroupAllResult | null {
    const matches: any = RegExp.prototype.exec.call(this.regexp, string);
    if (matches === null) {
      return matches;
    }
    const firstIndex = matches.index;

    const result: ExecGroupResult[] = [];
    for (let i = 0; i < this.groups.length; i++) {
      const group = this.groups[i];
      const mapped = group.index;
      const r: ExecGroupResult = {
        match: matches[mapped],
        start:
          firstIndex +
          group.previousGroups.reduce((sum: number, i: number) => sum + (matches[i] ? matches[i].length : 0), 0),
        end: 0,
      };
      r.end = r.start + (matches[mapped] ? matches[mapped].length : 0);

      result.push(r);
    }
    return Object.assign(result, {
      fullMatch: matches,
    });
  }

  toString() {
    return this.rawRegExp;
  }
}
